import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {
  Beanie,
  OllieHead,
  PlushPiMark,
  Sparkle,
  type Mood,
} from './feltFriends';
import {Bounce, Hop, ToyShell, type MapToyProps} from './mapToys';
import {playTones, type Tone} from './toySounds';

/**
 * More Stanford map toys, beside `stanfordMapToys.tsx`: Lake Lagunita (dry
 * most winters, full in a wet one), Memorial Church's organ and mosaic, the
 * Marguerite shuttle picking up a friend, a cow from the Farm, and a
 * jacaranda dropping its blossoms. Drawn generically in felt, with no
 * official marks and no visible text; each toy's status is in its
 * accessible name and is announced by {@link ToyShell}.
 */

const SANDSTONE = '#EAD6AE';
const SANDSTONE_DARK = '#C9B48A';
const TERRACOTTA = '#C8553D';
const WATER = '#7FB8D8';
const GRASS = '#9CC48A';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

/** A uniform whole number in [`low`, `high`]. */
function randomInt({low, high}: {low: number; high: number}): number {
  return low + Math.floor(Math.random() * (high - low + 1));
}

/* ── Lake Lagunita ─────────────────────────────────────────────────────── */

/** Chance a winter fills Lake Lagunita; most winters leave it dry. */
const LAKE_FULL_CHANCE = 1 / 4;

const QUACK: readonly Tone[] = [
  {frequency: 392, startSec: 0, durationSec: 0.08},
  {frequency: 330, startSec: 0.12, durationSec: 0.12},
];

const SPLASH: readonly Tone[] = [988, 1318.5, 1568, 1975.5].map(
  (frequency, index) => ({
    frequency,
    startSec: index * 0.06,
    durationSec: 0.12,
  }),
);

/** A felt mallard, about 14 units long, facing right, feet at the origin. */
function Duck({isSwimming}: {isSwimming: boolean}) {
  return (
    <g>
      {!isSwimming && (
        <path
          d="M -1.5 -2 V 0 M 1.5 -2 V 0"
          stroke="#E39A3B"
          strokeWidth={0.9}
          strokeLinecap="round"
        />
      )}
      <ellipse cx={0} cy={-4.6} rx={6} ry={3.6} fill="#F3EFE6" />
      <path d="M -6 -5.4 L -8.4 -7 L -6.4 -3.6 Z" fill="#F3EFE6" />
      <ellipse cx={-0.8} cy={-5.2} rx={3.2} ry={1.6} fill="#8A7A66" />
      <circle cx={4.4} cy={-8.4} r={2.6} fill="#2E7D5B" />
      <path d="M 6.6 -8.6 L 9 -8 L 6.6 -7.4 Z" fill="#E39A3B" />
      <circle cx={5} cy={-9} r={0.45} fill={FELT.ink} />
      <path
        d="M 2.4 -6.4 H 6"
        stroke="#FFFFFF"
        strokeWidth={0.6}
        strokeLinecap="round"
      />
    </g>
  );
}

/**
 * Lake Lagunita, which is dry most of the year. Each press brings a new
 * winter: usually the lake stays dry and a duck stands on the cracked mud,
 * puzzled; one winter in four it fills, and the ducks swim.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyLakeLagunita({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [winters, setWinters] = useState(0);
  const [isFull, setIsFull] = useState(false);

  const caption =
    winters === 0
      ? 'dry, as it is most of the year'
      : isFull
        ? 'a wet winter! The lake is full, and the ducks are swimming'
        : 'another dry winter. The duck is puzzled';

  return (
    <ToyShell
      isPreview={isPreview}
      label="Lake Lagunita"
      caption={caption}
      onPoke={() => {
        const willFill = Math.random() < LAKE_FULL_CHANCE;
        setWinters(winters + 1);
        setIsFull(willFill);
        playTones({tones: willFill ? SPLASH : QUACK, wave: 'triangle'});
      }}
    >
      <svg
        viewBox="0 0 100 64"
        width={size}
        height={size * 0.64}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={50}
          cy={44}
          rx={46}
          ry={16}
          fill={GRASS}
          filter={`url(#${ids.fuzz})`}
        />
        <ellipse cx={50} cy={45} rx={38} ry={11.5} fill="#D9C49A" />
        <path
          d="M 26 44 L 34 41 L 40 46 M 34 41 L 37 36 M 52 38 L 56 44 L 64 42 M 56 44 L 54 51 M 66 47 L 73 45"
          stroke="#B89F72"
          strokeWidth={0.9}
          fill="none"
          strokeLinecap="round"
        />
        <Water $isFull={isFull}>
          <ellipse cx={50} cy={45} rx={38.5} ry={12} fill={WATER} />
          <path
            d="M 30 42 q 3 -2 6 0 M 58 40 q 3 -2 6 0 M 44 49 q 3 -2 6 0"
            stroke="#FFFFFF"
            strokeWidth={0.9}
            fill="none"
            strokeLinecap="round"
            opacity={0.8}
          />
        </Water>
        {[
          {x: 9, flip: 1},
          {x: 91, flip: -1},
        ].map(({x, flip}) => (
          <path
            key={x}
            d={`M ${x} 46 q ${flip * 1} -9 ${flip * -1} -14 M ${x + flip * 3} 46 q ${flip * 1} -7 ${flip * 3} -11 M ${x - flip * 2} 46 q 0 -6 ${flip * -2} -9`}
            stroke="#5E8F4E"
            strokeWidth={1.4}
            fill="none"
            strokeLinecap="round"
          />
        ))}
        {isFull ? (
          <Hop key={`swim-${winters}`}>
            <g transform="translate(38 47)">
              <Duck isSwimming />
            </g>
            <g transform="translate(62 43) scale(-0.85 0.85)">
              <Duck isSwimming />
            </g>
          </Hop>
        ) : (
          <g key={`stand-${winters}`}>
            <Bounce as="g" $animation="pop">
              <g
                transform={`translate(50 46) rotate(${winters === 0 ? 0 : -12} 4 -8)`}
              >
                <Duck isSwimming={false} />
              </g>
            </Bounce>
          </g>
        )}
      </svg>
    </ToyShell>
  );
}

const Water = styled.g<{$isFull: boolean}>`
  opacity: ${({$isFull}) => ($isFull ? 1 : 0)};
  transition: opacity 0.7s ease-out;
  ${reducedMotion}
`;

/* ── Memorial Church ───────────────────────────────────────────────────── */

/** Organ chords the church cycles through: C, F, G, then C an octave up. */
const ORGAN_CHORDS: readonly (readonly number[])[] = [
  [261.6, 329.6, 392],
  [349.2, 440, 523.3],
  [392, 493.9, 587.3],
  [523.3, 659.3, 784],
];

const glint = keyframes`
  0% { opacity: 0; transform: scale(0.4); }
  40% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0; transform: scale(0.8); }
`;

/** A sparkle on the mosaic that flashes once, gently. */
const Glint = styled.g<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: ${glint} 1.1s ease-out ${({$delaySec}) => $delaySec}s 1;
  ${reducedMotion}
`;

const riseNote = keyframes`
  0% { opacity: 0; transform: translate(0, 0); }
  25% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--drift), -26px); }
`;

/** A music note rising from the church doors. */
const RisingNote = styled.g<{$delaySec: number; $drift: number}>`
  --drift: ${({$drift}) => $drift}px;
  opacity: 0;
  animation: ${riseNote} 1.6s ease-out ${({$delaySec}) => $delaySec}s 1;
  ${reducedMotion}
`;

/** Robe colours for the mosaic's figures. */
const ROBES = ['#4F86C6', '#C8553D', '#4FB58A', '#F6E9D3', '#8E6CC8'];

/** The figures across the mosaic, left to right; the middle one stands taller. */
const FIGURES = [
  {x: 30, scale: 0.8},
  {x: 37, scale: 0.85},
  {x: 44, scale: 0.9},
  {x: 50, scale: 1.1},
  {x: 56, scale: 0.9},
  {x: 63, scale: 0.85},
  {x: 70, scale: 0.8},
] as const;

/**
 * Memorial Church, with the gold mosaic across its front. Each press plays
 * a chord on the organ, sends notes out of the doors, and catches the sun
 * on the mosaic.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyMemorialChurch({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [chords, setChords] = useState(0);

  return (
    <ToyShell
      isPreview={isPreview}
      label="Memorial Church"
      caption={
        chords === 0
          ? 'the mosaic catches the light'
          : 'the organ plays a chord, and the mosaic glints'
      }
      onPoke={() => {
        const chord = ORGAN_CHORDS[chords % ORGAN_CHORDS.length] ?? [];
        playTones({
          tones: chord.map(frequency => ({
            frequency,
            startSec: 0,
            durationSec: 1.4,
          })),
        });
        setChords(chords + 1);
      }}
    >
      <svg
        viewBox="0 0 100 90"
        width={size}
        height={size * 0.9}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <path
          d="M 10 88 V 40 L 50 22 L 90 40 V 88 Z"
          fill={SANDSTONE}
          filter={`url(#${ids.fuzz})`}
        />
        <path
          d="M 7 41 L 50 20 L 93 41"
          stroke={TERRACOTTA}
          strokeWidth={3}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M 20 56 V 41 L 50 27 L 80 41 V 56 Z" fill="#E7C766" />
        <path
          d="M 20 56 V 41 L 50 27 L 80 41 V 56 Z"
          fill="none"
          stroke="#C9A43E"
          strokeWidth={1}
        />
        {FIGURES.map((figure, index) => (
          <g
            key={figure.x}
            transform={`translate(${figure.x} 54) scale(${figure.scale})`}
          >
            <path
              d="M -2.6 0 L -1.8 -9 Q 0 -10.4 1.8 -9 L 2.6 0 Z"
              fill={ROBES[index % ROBES.length]}
            />
            <circle cx={0} cy={-11.4} r={1.7} fill="#F2D7B6" />
            <circle
              cx={0}
              cy={-11.4}
              r={2.7}
              fill="none"
              stroke="#F6E9D3"
              strokeWidth={0.6}
            />
          </g>
        ))}
        <path d="M 10 58 H 90" stroke={SANDSTONE_DARK} strokeWidth={1.6} />
        {[32, 50, 68].map(x => (
          <path
            key={x}
            d={`M ${x - 7} 88 V 70 A 7 7 0 0 1 ${x + 7} 70 V 88 Z`}
            fill="#8A6A4A"
          />
        ))}
        {[16, 84].map(x => (
          <rect
            key={x}
            x={x - 1.4}
            y={60}
            width={2.8}
            height={28}
            fill={SANDSTONE_DARK}
          />
        ))}
        {chords > 0 && (
          <g key={chords}>
            {[
              {x: 30, y: 40, delaySec: 0},
              {x: 58, y: 33, delaySec: 0.25},
              {x: 70, y: 44, delaySec: 0.5},
            ].map(({x, y, delaySec}) => (
              <Glint key={x} $delaySec={delaySec}>
                <Sparkle x={x} y={y} r={3.2} />
              </Glint>
            ))}
            {[
              {x: 46, drift: -8, delaySec: 0},
              {x: 52, drift: 6, delaySec: 0.3},
              {x: 50, drift: -2, delaySec: 0.6},
            ].map(({x, drift, delaySec}) => (
              <RisingNote key={x} $delaySec={delaySec} $drift={drift}>
                <path
                  d={`M ${x} 70 V 62 L ${x + 4} 61 V 69`}
                  stroke={FELT.cardinal}
                  strokeWidth={0.9}
                  fill="none"
                />
                <ellipse
                  cx={x - 1.3}
                  cy={70}
                  rx={1.6}
                  ry={1.2}
                  fill={FELT.cardinal}
                />
                <ellipse
                  cx={x + 2.7}
                  cy={69}
                  rx={1.6}
                  ry={1.2}
                  fill={FELT.cardinal}
                />
              </RisingNote>
            ))}
          </g>
        )}
      </svg>
    </ToyShell>
  );
}

/* ── The Marguerite ────────────────────────────────────────────────────── */

/** The friends who wait at the stop, in turn. */
const RIDERS = ['Ollie', 'Beanie', 'Plush π'] as const;

/** A rider's name. */
type Rider = (typeof RIDERS)[number];

/** The average wait for the shuttle, in minutes. */
const MEAN_WAIT_MINUTES = 7;

/** A rider's face, about 18 units across, centered at the origin. */
function RiderFace({rider, mood}: {rider: Rider; mood: Mood}) {
  switch (rider) {
    case 'Ollie':
      return <OllieHead mood={mood} />;
    case 'Beanie':
      return (
        <g transform="scale(1.4)">
          <Beanie mood={mood} />
        </g>
      );
    case 'Plush π':
      return <PlushPiMark mood={mood} />;
  }
}

const drive = keyframes`
  0% { transform: translateX(-140px); }
  38% { transform: translateX(0); }
  62% { transform: translateX(0); }
  100% { transform: translateX(150px); }
`;

/** The shuttle's run: in from the left, a stop, then off to the right. */
const Run = styled.g`
  transform: translateX(150px);
  animation: ${drive} 2.6s ease-in-out 1 forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translateX(0);
  }
`;

const board = keyframes`
  0%, 45% { opacity: 1; }
  52%, 100% { opacity: 0; }
`;

/** The rider at the stop, who steps aboard while the shuttle waits. */
const Boarding = styled.g`
  animation: ${board} 2.6s linear 1 forwards;
  ${reducedMotion}
`;

const peek = keyframes`
  0%, 50% { opacity: 0; }
  56%, 100% { opacity: 1; }
`;

/** The rider's face in the back window once aboard. */
const Aboard = styled.g`
  opacity: 0;
  animation: ${peek} 2.6s linear 1 forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

/** A felt campus shuttle, about 64 units long, wheels on y = 52. */
function Shuttle() {
  return (
    <g>
      <rect x={0} y={20} width={64} height={30} rx={6} fill="#F6F2EA" />
      <rect x={0} y={40} width={64} height={5} fill={FELT.cardinal} />
      <rect x={52} y={24} width={10} height={14} rx={2} fill="#BFDDEB" />
      {[6, 18, 30, 42].map(x => (
        <rect
          key={x}
          x={x}
          y={24}
          width={9}
          height={11}
          rx={1.6}
          fill="#BFDDEB"
        />
      ))}
      {[13, 50].map(cx => (
        <g key={cx}>
          <circle cx={cx} cy={50} r={5} fill="#3E4452" />
          <circle cx={cx} cy={50} r={2} fill="#C9CED6" />
        </g>
      ))}
    </g>
  );
}

/**
 * The Marguerite, Stanford's free campus shuttle. A friend waits at the
 * stop; each press, the shuttle comes after a random wait, picks them up
 * and drives off, and the next friend steps up to wait.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyMarguerite({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [runs, setRuns] = useState(0);
  const [waitMinutes, setWaitMinutes] = useState(0);
  const waiting = RIDERS[runs % RIDERS.length] ?? 'Ollie';
  const lastRider =
    RIDERS[(runs + RIDERS.length - 1) % RIDERS.length] ?? 'Ollie';

  return (
    <ToyShell
      isPreview={isPreview}
      label="The Marguerite"
      caption={
        runs === 0
          ? `${waiting} is waiting for the shuttle`
          : `the shuttle came after ${waitMinutes} ${waitMinutes === 1 ? 'minute' : 'minutes'} and picked up ${lastRider}. Now ${waiting} is waiting`
      }
      onPoke={() => {
        // Waits between shuttles are roughly exponential, like any bus
        // that keeps no timetable you can see.
        const minutes = Math.max(
          1,
          Math.round(-MEAN_WAIT_MINUTES * Math.log(1 - Math.random())),
        );
        setWaitMinutes(minutes);
        setRuns(runs + 1);
        playTones({
          tones: [
            {frequency: 523.3, startSec: 0.9, durationSec: 0.18},
            {frequency: 659.3, startSec: 1.1, durationSec: 0.24},
          ],
          wave: 'triangle',
        });
      }}
    >
      <svg
        viewBox="0 0 120 64"
        width={size}
        height={size * (64 / 120)}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
          <clipPath id={`${ids.fuzz}-road`}>
            <rect x={-4} y={0} width={128} height={64} />
          </clipPath>
        </defs>
        <rect x={-4} y={55} width={128} height={6} rx={3} fill="#C9CED6" />
        <path
          d="M 4 58 H 20 M 32 58 H 48 M 60 58 H 76 M 88 58 H 104"
          stroke="#FFFFFF"
          strokeWidth={1}
          strokeLinecap="round"
        />
        <path d="M 104 55 V 22" stroke="#6B7280" strokeWidth={1.6} />
        <circle cx={104} cy={20} r={6} fill={FELT.cardinal} />
        <rect
          x={100.6}
          y={17.4}
          width={6.8}
          height={4.6}
          rx={1.2}
          fill="#FFFFFF"
        />
        {runs > 0 && (
          <Boarding key={`boarding-${runs}`}>
            <g transform="translate(92 44) scale(0.8)">
              <RiderFace rider={lastRider} mood="happy" />
            </g>
          </Boarding>
        )}
        {runs === 0 ? (
          <g transform="translate(92 44) scale(0.8)">
            <RiderFace rider={waiting} mood="calm" />
          </g>
        ) : (
          <Hop
            key={`next-${runs}`}
            style={{animationDelay: '2.3s', animationFillMode: 'both'}}
          >
            <g transform="translate(92 44) scale(0.8)">
              <RiderFace rider={waiting} mood="calm" />
            </g>
          </Hop>
        )}
        <g clipPath={`url(#${ids.fuzz}-road)`}>
          {runs > 0 && (
            <Run key={runs}>
              <g transform="translate(26 4)">
                <Shuttle />
                <Aboard>
                  <g transform="translate(10.5 29.5) scale(0.42)">
                    <RiderFace rider={lastRider} mood="happy" />
                  </g>
                </Aboard>
              </g>
            </Run>
          )}
        </g>
      </svg>
    </ToyShell>
  );
}

/* ── A cow from the Farm ───────────────────────────────────────────────── */

/** Where a cow's spots may be, each present with even odds. */
const SPOT_SPOTS = [
  {cx: 26, cy: 30, rx: 4, ry: 3},
  {cx: 36, cy: 26, rx: 3.4, ry: 2.6},
  {cx: 44, cy: 34, rx: 4.6, ry: 3.4},
  {cx: 54, cy: 27, rx: 3.2, ry: 2.4},
  {cx: 31, cy: 39, rx: 3, ry: 2.2},
  {cx: 58, cy: 37, rx: 3.6, ry: 2.6},
  {cx: 22, cy: 37, rx: 2.4, ry: 2},
] as const;

/** A fresh coat: each spot with even odds, and never a spotless cow. */
function drawSpots(): boolean[] {
  const spots = SPOT_SPOTS.map(() => Math.random() < 0.5);
  if (!spots.includes(true)) {
    spots[randomInt({low: 0, high: spots.length - 1})] = true;
  }
  return spots;
}

const MOO: readonly Tone[] = [
  {frequency: 196, startSec: 0, durationSec: 0.35},
  {frequency: 174.6, startSec: 0.3, durationSec: 0.5},
];

/**
 * A cow from the Farm, Stanford's nickname from its days as Leland
 * Stanford's stock farm; cattle still graze the hills by the Dish. Each
 * press, it moos and a new cow wanders over with its own random spots.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyFarmCow({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [moos, setMoos] = useState(0);
  const [spots, setSpots] = useState<boolean[]>(drawSpots);
  const spotCount = spots.filter(Boolean).length;

  return (
    <ToyShell
      isPreview={isPreview}
      label="A cow from the Farm"
      caption={
        moos === 0
          ? `grazing, with ${spotCount} ${spotCount === 1 ? 'spot' : 'spots'}`
          : `moo! A new cow wanders over, with ${spotCount} ${spotCount === 1 ? 'spot' : 'spots'}`
      }
      onPoke={() => {
        setMoos(moos + 1);
        setSpots(drawSpots());
        playTones({tones: MOO, wave: 'triangle'});
      }}
    >
      <svg
        viewBox="0 0 90 66"
        width={size}
        height={size * (66 / 90)}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={45}
          cy={60}
          rx={42}
          ry={6}
          fill={GRASS}
          filter={`url(#${ids.fuzz})`}
        />
        <g key={moos}>
          <Bounce as="g" $animation="pop">
            {[24, 32, 52, 60].map(x => (
              <rect
                key={x}
                x={x - 2}
                y={42}
                width={4}
                height={15}
                rx={1.6}
                fill="#F3EFE6"
              />
            ))}
            {[24, 32, 52, 60].map(x => (
              <rect
                key={`hoof-${x}`}
                x={x - 2}
                y={54}
                width={4}
                height={3}
                rx={1}
                fill="#4B4440"
              />
            ))}
            <path
              d="M 16 30 Q 10 38 12 46"
              stroke="#F3EFE6"
              strokeWidth={1.6}
              fill="none"
              strokeLinecap="round"
            />
            <circle cx={12} cy={47} r={1.8} fill="#4B4440" />
            <rect
              x={16}
              y={20}
              width={50}
              height={26}
              rx={12}
              fill="#F3EFE6"
              filter={`url(#${ids.fuzz})`}
            />
            {SPOT_SPOTS.map((spot, index) =>
              spots[index] ? (
                <ellipse
                  key={`${spot.cx}-${spot.cy}`}
                  cx={spot.cx}
                  cy={spot.cy}
                  rx={spot.rx}
                  ry={spot.ry}
                  fill="#3E3733"
                />
              ) : null,
            )}
            <ellipse cx={41} cy={46} rx={5} ry={2.4} fill={FELT.pink} />
            <path
              d="M 66 18 L 64 13 M 76 18 L 78 13"
              stroke="#D8C7A8"
              strokeWidth={1.6}
              strokeLinecap="round"
            />
            <ellipse
              cx={62}
              cy={20}
              rx={4}
              ry={2}
              fill="#F3EFE6"
              transform="rotate(-20 62 20)"
            />
            <ellipse
              cx={80}
              cy={20}
              rx={4}
              ry={2}
              fill="#F3EFE6"
              transform="rotate(20 80 20)"
            />
            <ellipse cx={71} cy={26} rx={8.4} ry={9} fill="#F3EFE6" />
            <ellipse cx={71} cy={32} rx={6.6} ry={4.4} fill="#F4B9C4" />
            <circle cx={68.6} cy={32} r={0.8} fill="#9C5A68" />
            <circle cx={73.4} cy={32} r={0.8} fill="#9C5A68" />
            <g transform="translate(71 22) scale(1.3)">
              <circle cx={-2.4} cy={0.6} r={0.65} fill={FELT.ink} />
              <circle cx={2.4} cy={0.6} r={0.65} fill={FELT.ink} />
            </g>
          </Bounce>
        </g>
        {moos > 0 && (
          <Hop key={`moo-${moos}`}>
            <path
              d="M 84 10 q 3 -2 6 0 M 86 5 q 3 -2 6 0"
              stroke="#6B7280"
              strokeWidth={0.9}
              fill="none"
              strokeLinecap="round"
            />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}

/* ── Jacaranda ─────────────────────────────────────────────────────────── */

/** Blossoms that fall on each press. */
const BLOSSOMS_PER_PRESS = 7;

/** The most fallen blossoms kept on the ground. */
const MAX_FALLEN = 42;

/** A blossom on its way down: where it starts and where it lands. */
interface Blossom {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

const fall = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) rotate(200deg); opacity: 1; }
`;

/** One blossom drifting from the canopy to the ground. */
const Falling = styled.g<{$dx: number; $dy: number; $delaySec: number}>`
  --dx: ${({$dx}) => $dx}px;
  --dy: ${({$dy}) => $dy}px;
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: ${fall} 1.6s ease-in ${({$delaySec}) => $delaySec}s 1 forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: translate(var(--dx), var(--dy));
  }
`;

const LAVENDER = ['#8E6CC8', '#A88BDB', '#7B5CB8', '#B9A0E6'];

/** A lavender canopy puff. */
const CANOPY = [
  {cx: 26, cy: 30, r: 12},
  {cx: 40, cy: 22, r: 14},
  {cx: 55, cy: 26, r: 13},
  {cx: 34, cy: 38, r: 11},
  {cx: 50, cy: 38, r: 12},
  {cx: 62, cy: 36, r: 9},
  {cx: 18, cy: 40, r: 8},
];

/**
 * A jacaranda in bloom, as on campus in late spring. Each press shakes
 * loose a few purple blossoms that drift down and gather on the grass.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyJacaranda({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [falling, setFalling] = useState<Blossom[]>([]);
  const [fallen, setFallen] = useState<Blossom[]>([]);
  const [shakes, setShakes] = useState(0);

  const shake = () => {
    const next = Array.from({length: BLOSSOMS_PER_PRESS}, (_, index) => ({
      id: shakes * BLOSSOMS_PER_PRESS + index,
      fromX: randomInt({low: 18, high: 62}),
      fromY: randomInt({low: 26, high: 42}),
      toX: randomInt({low: 8, high: 74}),
      toY: randomInt({low: 80, high: 86}),
    }));
    setFallen([...fallen, ...falling].slice(-MAX_FALLEN));
    setFalling(next);
    setShakes(shakes + 1);
    playTones({
      tones: [1318.5, 1174.7, 987.8].map((frequency, index) => ({
        frequency,
        startSec: index * 0.15,
        durationSec: 0.3,
      })),
    });
  };
  const onGround = fallen.length + falling.length;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Jacaranda"
      caption={
        shakes === 0
          ? 'in full purple bloom'
          : `purple blossoms drift down; ${onGround} on the grass`
      }
      onPoke={shake}
    >
      <svg
        viewBox="0 0 80 90"
        width={size}
        height={size * (90 / 80)}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={40}
          cy={84}
          rx={38}
          ry={5.6}
          fill={GRASS}
          filter={`url(#${ids.fuzz})`}
        />
        <path
          d="M 38 84 C 38 70 36 60 40 46 M 39 62 C 32 56 28 50 26 44 M 40 56 C 46 50 52 46 56 40"
          stroke="#7A5A43"
          strokeWidth={3.4}
          fill="none"
          strokeLinecap="round"
        />
        {CANOPY.map((puff, index) => (
          <circle
            key={`${puff.cx}-${puff.cy}`}
            cx={puff.cx}
            cy={puff.cy}
            r={puff.r}
            fill={LAVENDER[index % LAVENDER.length]}
            filter={`url(#${ids.fuzz})`}
          />
        ))}
        {fallen.map(blossom => (
          <circle
            key={blossom.id}
            cx={blossom.toX}
            cy={blossom.toY}
            r={1.3}
            fill={LAVENDER[blossom.id % LAVENDER.length]}
          />
        ))}
        {falling.map((blossom, index) => (
          <Falling
            key={blossom.id}
            $dx={blossom.toX - blossom.fromX}
            $dy={blossom.toY - blossom.fromY}
            $delaySec={index * 0.12}
          >
            <circle
              cx={blossom.fromX}
              cy={blossom.fromY}
              r={1.4}
              fill={LAVENDER[blossom.id % LAVENDER.length]}
            />
          </Falling>
        ))}
      </svg>
    </ToyShell>
  );
}
